export type ServiceCategory =
	| 'AI'
	| 'Mobile'
	| 'Automation'
	| 'Data'
	| 'Backend'
	| 'Full-Stack'
	| 'Extensions';

export type ServiceProcessStep = {
	step: string;
	detail: string;
	output?: string;
};

export type ServiceScopeItem = {
	id: string;
	title: string;
	categoryTag: string;
	description: string;
	tags: string[];
};

export type StackGroup = {
	group: string;
	items: string[];
};

export type ServiceRelatedWork = {
	title: string;
	slug: string;
	badge: string;
	description: string;
	metrics: { label: string; value: string }[];
	tech: string[];
};

export type Service = {
	slug: string;
	category: ServiceCategory;
	/** Short machine tag shown on the overview card, e.g. "#AGENTS". */
	categoryLabel: string;
	title: string;
	/** Iconify icon name, e.g. "lucide:bot". Rendered on cards + detail hero. */
	icon: string;
	/** Lead flagship service. Only one should be true. */
	flagship?: boolean;
	/** Card tagline (overview grid) + hero subtitle (detail). */
	tagline: string;
	/** Tech badge pills on the overview card. */
	stack: string[];
	/** Categorized badges on the detail ~/STACK section. */
	stackGroups: StackGroup[];
	/** Capability cards on the detail ~/SCOPE section. */
	scope: ServiceScopeItem[];
	/** 4-stage pipeline: SCOPE → ARCHITECT → EXECUTE → HARDEN. */
	process: ServiceProcessStep[];
	/** Runtime parameters rendered in the telemetry spec card. */
	telemetry: { key: string; value: string }[];
	/** Linked case studies on the detail ~/WORK/CASE-STUDY section. */
	relatedWork?: ServiceRelatedWork[];
};

export const services: Service[] = [
	{
		slug: 'ai-agent-development',
		category: 'AI',
		categoryLabel: '#AGENTS',
		icon: 'lucide:bot',
		title: 'AI Agent Development',
		flagship: true,
		tagline:
			'Autonomous agents that plan, use tools, and get real work done, not chatbots.',
		stack: ['OpenAI', 'Claude', 'LangChain', 'LangGraph', 'Python', 'Node.js'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Python', 'Node.js', 'TypeScript'] },
			{ group: 'REASONING', items: ['OpenAI', 'Claude', 'GPT-4o', 'o1'] },
			{
				group: 'FRAMEWORK',
				items: ['LangChain', 'LangGraph', 'CrewAI', 'OpenAI Agents'],
			},
			{
				group: 'STATE CONTROL',
				items: ['Mem0', 'pgvector', 'Redis', 'Semantic memory'],
			},
			{ group: 'EVENT RUNTIME', items: ['BullMQ', 'WebSockets', 'tRPC'] },
		],
		scope: [
			{
				id: 'AUTONOMY',
				title: 'Task planning & tool use',
				categoryTag: '#AUTONOMY',
				description:
					'Agents built for action, they plan multi-step tasks, call your tools, and drive them to completion without a human in the loop.',
				tags: [
					'Task decomposition',
					'Tool calling',
					'ReAct loops',
					'Reflection',
				],
			},
			{
				id: 'ORCHESTRATION',
				title: 'Multi-agent systems',
				categoryTag: '#MESH',
				description:
					"Multiple specialized agents that collaborate on complex workflows, planner, executor, reviewer, with hand-offs that don't lose context.",
				tags: [
					'LangGraph',
					'Swarm patterns',
					'Hand-off protocols',
					'Supervisor agents',
				],
			},
			{
				id: 'MEMORY',
				title: 'Memory & context',
				categoryTag: '#MEMORY',
				description:
					'Working, long-term, and semantic memory so the agent learns from every run instead of starting cold each time.',
				tags: ['Vector memory', 'Summaries', 'Personalization', 'Recency'],
			},
			{
				id: 'GUARDRAILS',
				title: 'Safety & guardrails',
				categoryTag: '#DEFENSE',
				description:
					'Cost limits, human-in-the-loop checkpoints, input/output validation, and failure handling so autonomy never means runaway spend.',
				tags: ['Cost caps', 'HITL checkpoints', 'Validation', 'Fallbacks'],
			},
			{
				id: 'TOOLS',
				title: 'Tool integrations',
				categoryTag: '#TOOLS',
				description:
					'Everything your business already uses becomes an agent "tool", CRMs, databases, internal APIs, email, and file systems.',
				tags: ['Custom tools', 'OpenAPI specs', 'Auth scoping', 'Webhooks'],
			},
			{
				id: 'OBSERVABILITY',
				title: 'Evaluation & monitoring',
				categoryTag: '#TRACE',
				description:
					'Trace every step, score outcomes, and catch behavioral drift before it silently degrades in production.',
				tags: ['Tracing', 'Evals', 'Alerts', 'Cost telemetry'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail:
					"Define exactly what the agent should (and shouldn't) do autonomously.",
				output: 'Autonomy contract',
			},
			{
				step: 'ARCHITECT',
				detail:
					'Design the loop, tool access, memory layer, and stop conditions.',
				output: 'Agent architecture diagram',
			},
			{
				step: 'EXECUTE',
				detail:
					'Build with real tool calls, not mocked ones, and test edge cases.',
				output: 'Deployable agent build',
			},
			{
				step: 'HARDEN',
				detail:
					'Guardrails, cost limits, logging, and fallback behavior before production.',
				output: 'Production runbook',
			},
		],
		telemetry: [
			{ key: 'agent_type', value: 'autonomous_orchestrator' },
			{ key: 'orchestration_engine', value: 'LangGraph' },
			{ key: 'inference_gate', value: 'cost_capped_router' },
			{ key: 'runtime', value: 'python_3.12 · node_20' },
		],
		relatedWork: [
			{
				title: 'GPT Guider',
				slug: 'gpt-guider',
				badge: 'AI learning companion',
				description:
					'A guided AI tutor that plans lessons, adapts to your pace, and routes every response through a cost-aware inference gate.',
				metrics: [
					{ label: 'Task accuracy', value: '99.4%' },
					{ label: 'Response latency', value: '<450ms' },
					{ label: 'Uncaught loops', value: '0' },
				],
				tech: ['OpenAI', 'LangChain', 'Next.js', 'Streaming'],
			},
		],
	},
	{
		slug: 'ai-voice-agents',
		category: 'AI',
		categoryLabel: '#VOICE',
		icon: 'lucide:mic',
		title: 'AI Voice Agents',
		tagline: 'Phone agents that answer, qualify and book, 24/7.',
		stack: ['Vapi', 'Retell', 'ElevenLabs', 'Twilio', 'OpenAI', 'Claude'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Node.js', 'TypeScript', 'Python'] },
			{ group: 'REASONING', items: ['OpenAI', 'Claude', 'Realtime voice'] },
			{ group: 'FRAMEWORK', items: ['Vapi', 'Retell', 'ElevenLabs'] },
			{
				group: 'STATE CONTROL',
				items: ['Twilio', 'SIP trunking', 'Call recording'],
			},
			{
				group: 'BACKEND CORE',
				items: ['CRM webhooks', 'Calendar sync', 'Auth scoping'],
			},
		],
		scope: [
			{
				id: 'INBOUND',
				title: 'Inbound answering',
				categoryTag: '#VOICE',
				description:
					'Every call answered in seconds, with greeting, intent detection and smart routing. No missed calls, no voicemail black holes.',
				tags: ['Greeting flows', 'Intent routing', 'Webhooks'],
			},
			{
				id: 'OUTBOUND',
				title: 'Outbound campaigns',
				categoryTag: '#ON',
				description:
					'Follow-ups, reminders, reactivations and review requests, dialed automatically within compliance limits.',
				tags: ['Cron', 'DNC filtering', 'Rate limits'],
			},
			{
				id: 'QUALIFY',
				title: 'Lead qualification',
				categoryTag: '#QUAL',
				description:
					'Budget, timeline and intent captured in natural conversation, scored and pushed to your pipeline.',
				tags: ['Structured output', 'Scoring rules', 'Webhooks'],
			},
			{
				id: 'BOOKING',
				title: 'Appointment booking',
				categoryTag: '#BOOK',
				description:
					'Reads real availability, books, reschedules and confirms. Clinics, real estate, restaurants and service businesses run on this.',
				tags: ['Calendar sync', 'Confirmations', 'Double-book guard'],
			},
			{
				id: 'SYNC',
				title: 'CRM and calendar sync',
				categoryTag: '#SYNC',
				description:
					'Every call logged with transcript, summary and outcome, synced to your CRM and calendar.',
				tags: ['Run logs', 'API sync', 'Notifications'],
			},
			{
				id: 'HANDOFF',
				title: 'Human handoff and analytics',
				categoryTag: '#TRACE',
				description:
					'Stuck calls transfer to a human with full context. Transcripts, recordings and dashboards show what callers want.',
				tags: ['Human hand-off', 'Dashboards', 'Evals'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail:
					'Which calls the agent handles, and where it must hand off to a human.',
				output: 'Call handling contract',
			},
			{
				step: 'ARCHITECT',
				detail: 'Voice stack, prompts, booking rules and CRM wiring.',
				output: 'Voice architecture spec',
			},
			{
				step: 'EXECUTE',
				detail:
					'Build against real numbers, test accents, noise and edge cases.',
				output: 'Working voice agent',
			},
			{
				step: 'HARDEN',
				detail:
					'Recording consent, cost caps, monitoring and fallback numbers.',
				output: 'Production runbook',
			},
		],
		telemetry: [
			{ key: 'voice_stack', value: 'vapi · retell · elevenlabs' },
			{ key: 'telephony', value: 'twilio_sip' },
			{ key: 'inference_gate', value: 'cost_capped_router' },
			{ key: 'compliance', value: 'recording_consent_on' },
		],
		relatedWork: [
			{
				title: 'GPT Guider',
				slug: 'gpt-guider',
				badge: 'Conversational AI tutor',
				description:
					'Spoken-style tutoring dialogues that plan lessons and adapt to pace, streamed through a cost-aware inference gate.',
				metrics: [
					{ label: 'Task accuracy', value: '99.4%' },
					{ label: 'Response latency', value: '<450ms' },
					{ label: 'Uncaught loops', value: '0' },
				],
				tech: ['OpenAI', 'LangChain', 'Next.js', 'Streaming'],
			},
		],
	},
	{
		slug: 'messaging-bots',
		category: 'AI',
		categoryLabel: '#CHAT',
		icon: 'lucide:message-circle',
		title: 'WhatsApp, Telegram and Messenger Bots',
		tagline: 'Sales and support bots where your customers already chat.',
		stack: [
			'WhatsApp Cloud API',
			'Telegram Bot API',
			'Meta Graph API',
			'Node.js',
			'OpenAI',
			'Claude',
		],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Node.js', 'TypeScript', 'Python'] },
			{ group: 'REASONING', items: ['OpenAI', 'Claude', 'Embeddings'] },
			{
				group: 'FRAMEWORK',
				items: ['WhatsApp Cloud API', 'Telegram Bot API', 'Meta Graph API'],
			},
			{
				group: 'STATE CONTROL',
				items: ['Redis cache', 'Sessions', 'Webhooks'],
			},
			{
				group: 'BACKEND CORE',
				items: ['Payments', 'CRM hooks', 'Auth'],
			},
		],
		scope: [
			{
				id: 'ORDERS',
				title: 'AI-assisted order taking',
				categoryTag: '#CART',
				description:
					'Customers browse, customize and pay without leaving chat. Orders land in your system, confirmed.',
				tags: ['Product catalog', 'Payments', 'Confirmations'],
			},
			{
				id: 'SUPPORT',
				title: 'FAQ and support',
				categoryTag: '#FAQ',
				description:
					'Instant answers from your docs and policies, with escalation to a human when it matters.',
				tags: ['Semantic search', 'Human hand-off', 'SLA enforcement'],
			},
			{
				id: 'BROADCAST',
				title: 'Broadcasts and templates',
				categoryTag: '#ON',
				description:
					'Promotions and updates via approved templates, segmented and scheduled, without bans.',
				tags: ['Cron', 'Segmentation', 'Rate limits'],
			},
			{
				id: 'PAYMENTS',
				title: 'Payments and CRM hooks',
				categoryTag: '#REVENUE',
				description:
					'Collect payment in chat and sync every conversation to your CRM and sheets.',
				tags: ['Stripe', 'API sync', 'Receipts'],
			},
			{
				id: 'ADMIN',
				title: 'Admin dashboard',
				categoryTag: '#TOOLS',
				description:
					'Teach the bot, review chats and step in live from one dashboard.',
				tags: ['Dashboards', 'Human hand-off', 'Audit trail'],
			},
			{
				id: 'I18N',
				title: 'Multilingual conversations',
				categoryTag: '#PRODUCT',
				description:
					'Serves customers in their language, including Arabic and RTL layouts.',
				tags: ['i18n', 'RTL layouts', 'Auto detection'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail: 'Which chats the bot owns, and where a human takes over.',
				output: 'Conversation map',
			},
			{
				step: 'ARCHITECT',
				detail: 'Channel setup, templates, payments and CRM wiring.',
				output: 'Bot architecture spec',
			},
			{
				step: 'EXECUTE',
				detail: 'Build against real numbers, test templates and payments.',
				output: 'Working chat bot',
			},
			{
				step: 'HARDEN',
				detail: 'Rate limits, ban-safe sending and handoff before launch.',
				output: 'Production runbook',
			},
		],
		telemetry: [
			{ key: 'channels', value: 'whatsapp · telegram · messenger' },
			{ key: 'payments', value: 'in_chat_collect' },
			{ key: 'inference_gate', value: 'route·cache·stream' },
			{ key: 'sending_policy', value: 'template_safe' },
		],
		relatedWork: [
			{
				title: '20Lab',
				slug: '20lab',
				badge: 'Chat commerce automation',
				description:
					'Order and catalog pipelines that keep storefront chats stocked and synced, without manual exports.',
				metrics: [
					{ label: 'Workflows automated', value: '8+' },
					{ label: 'Sync reliability', value: '99.9%' },
					{ label: 'Manual hours saved', value: '12/wk' },
				],
				tech: ['Python', 'Playwright', 'n8n', 'Supabase'],
			},
		],
	},
	{
		slug: 'ai-app-rescue',
		category: 'Full-Stack',
		categoryLabel: '#RESCUE',
		icon: 'lucide:life-buoy',
		title: 'AI App Rescue',
		tagline:
			'Built it with Lovable, Bolt or Cursor and got stuck? I fix and ship it.',
		stack: ['Next.js', 'Supabase', 'Stripe', 'Auth', 'Vercel', 'Playwright'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Next.js', 'TypeScript', 'React'] },
			{ group: 'FRAMEWORK', items: ['Supabase', 'Auth', 'Stripe'] },
			{
				group: 'STATE CONTROL',
				items: ['PostgreSQL', 'Migrations', 'Backups'],
			},
			{ group: 'BACKEND CORE', items: ['Vercel', 'Edge functions', 'CI/CD'] },
			{ group: 'EVENT RUNTIME', items: ['Playwright', 'E2E', 'Monitoring'] },
		],
		scope: [
			{
				id: 'AUDIT',
				title: 'Fixed-price code audit',
				categoryTag: '#SPEC',
				description:
					'Full review of your AI-built app: bugs, security holes and what blocks launch, priced before work starts.',
				tags: ['Bug triage', 'E2E', 'Audit trail'],
			},
			{
				id: 'FIX',
				title: 'Bug fixing',
				categoryTag: '#RESCUE',
				description:
					'Real fixes verified with tests, not patches that break next week.',
				tags: ['Unit tests', 'E2E', 'Regression tests'],
			},
			{
				id: 'AUTH',
				title: 'Auth and payments',
				categoryTag: '#SECURE',
				description:
					'Login, roles and Stripe wired correctly, with webhooks you can trust.',
				tags: ['OAuth 2', 'Stripe', 'Webhooks'],
			},
			{
				id: 'SECURE',
				title: 'Security review',
				categoryTag: '#HARDEN',
				description:
					'Exposed keys, open buckets and injection risks closed before launch.',
				tags: ['Scoped access', 'Sanitization', 'Security review'],
			},
			{
				id: 'SPEED',
				title: 'Performance',
				categoryTag: '#FAST',
				description:
					'Slow queries, heavy bundles and blocking renders fixed against measured budgets.',
				tags: ['Query plans', 'Code splitting', 'LCP'],
			},
			{
				id: 'SHIP',
				title: 'Deployment and handoff',
				categoryTag: '#LIVE',
				description:
					'Live on Vercel with CI, tests and docs your next dev can follow.',
				tags: ['CI/CD', 'Previews', 'Documentation'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail:
					'Audit the build: what is broken, what is risky, what blocks launch.',
				output: 'Audit report',
			},
			{
				step: 'ARCHITECT',
				detail: 'Fix plan with fixed price and a launch checklist.',
				output: 'Fix plan',
			},
			{
				step: 'EXECUTE',
				detail: 'Fix, secure and test, with weekly demos.',
				output: 'Working build',
			},
			{
				step: 'HARDEN',
				detail: 'Deploy, document and hand over.',
				output: 'Launch runbook',
			},
		],
		telemetry: [
			{ key: 'rescue_target', value: 'lovable · bolt · cursor' },
			{ key: 'data_layer', value: 'supabase_postgres' },
			{ key: 'qa_gate', value: 'playwright_e2e' },
			{ key: 'deploy', value: 'vercel_ci' },
		],
		relatedWork: [
			{
				title: 'Pie Coin',
				slug: 'pie-coin',
				badge: 'Rescued full-stack launch',
				description:
					'A stuck community build fixed, hardened and shipped: auth, live leaderboards and real-time updates in production.',
				metrics: [
					{ label: 'Community members', value: '2K+' },
					{ label: 'Real-time events', value: 'Live' },
					{ label: 'Deployment', value: 'Production' },
				],
				tech: ['Next.js', 'Supabase', 'tRPC'],
			},
		],
	},
	{
		slug: 'rag-knowledge-assistants',
		category: 'AI',
		categoryLabel: '#KNOW',
		icon: 'lucide:database-zap',
		title: 'Custom RAG Knowledge Assistants',
		tagline:
			'Chat over your docs, tickets and data, with sources and no made-up answers.',
		stack: ['pgvector', 'Pinecone', 'OpenAI', 'Claude', 'LangChain', 'Next.js'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Python', 'TypeScript', 'Next.js'] },
			{ group: 'REASONING', items: ['OpenAI', 'Claude', 'Embeddings'] },
			{
				group: 'FRAMEWORK',
				items: ['LangChain', 'RAG pipelines', 'Hybrid search'],
			},
			{
				group: 'STATE CONTROL',
				items: ['pgvector', 'Pinecone', 'Redis cache'],
			},
			{
				group: 'BACKEND CORE',
				items: ['Auth', 'Rate limiting', 'Webhooks'],
			},
		],
		scope: [
			{
				id: 'INGEST',
				title: 'Ingestion pipelines',
				categoryTag: '#PIPES',
				description:
					'Docs, tickets, PDFs and wikis flow in automatically and stay fresh.',
				tags: ['Incremental runs', 'Dedup', 'Freshness'],
			},
			{
				id: 'RETRIEVE',
				title: 'Chunking and retrieval tuning',
				categoryTag: '#VECTORS',
				description:
					'Chunk strategy and reranking tuned until answers cite the right passages.',
				tags: ['Chunking', 'Hybrid rerank', 'Evaluation'],
			},
			{
				id: 'CITE',
				title: 'Citations, no invented answers',
				categoryTag: '#SCHEMA',
				description:
					'Every answer links its sources. Unknown questions get an honest handoff, not a guess.',
				tags: ['Structured output', 'Validation', 'Human hand-off'],
			},
			{
				id: 'ACCESS',
				title: 'Permission-aware access',
				categoryTag: '#SECURE',
				description:
					'Users only see what they are allowed to see, enforced at retrieval time.',
				tags: ['RBAC', 'Scoped access', 'Audit trail'],
			},
			{
				id: 'EVAL',
				title: 'Evals',
				categoryTag: '#TRACE',
				description:
					'Groundedness and relevance scored on every deploy, drift caught early.',
				tags: ['Evals', 'Tracing', 'Cost telemetry'],
			},
			{
				id: 'FRONT',
				title: 'Widget, Slack or WhatsApp front end',
				categoryTag: '#PRODUCT',
				description:
					'Embed on your site or serve answers where the team already chats.',
				tags: ['Exports', 'Webhooks', 'Notifications'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail:
					'Which sources the assistant may answer from, and who sees what.',
				output: 'Source map',
			},
			{
				step: 'ARCHITECT',
				detail: 'Chunking, retrieval and permission model design.',
				output: 'RAG architecture spec',
			},
			{
				step: 'EXECUTE',
				detail: 'Build the pipeline, tune retrieval against real questions.',
				output: 'Working assistant',
			},
			{
				step: 'HARDEN',
				detail: 'Evals, citation checks and freshness loops in production.',
				output: 'Quality dashboard',
			},
		],
		telemetry: [
			{ key: 'retrieval', value: 'hybrid_rag · rerank' },
			{ key: 'sources', value: 'docs · tickets · wikis' },
			{ key: 'inference_gate', value: 'route·cache·stream' },
			{ key: 'validation', value: 'cited_answers_only' },
		],
		relatedWork: [
			{
				title: 'GPT Guider',
				slug: 'gpt-guider',
				badge: 'Curriculum RAG tutor',
				description:
					'Every answer grounded in the curriculum via RAG, streamed fast and cost-controlled per session.',
				metrics: [
					{ label: 'Answer grounding', value: '100%' },
					{ label: 'Stream start', value: '<250ms' },
					{ label: 'Cost / session', value: '-62%' },
				],
				tech: ['RAG', 'OpenAI', 'Streaming', 'pgvector'],
			},
		],
	},
	{
		slug: 'automation',
		category: 'Automation',
		categoryLabel: '#BOTS',
		icon: 'lucide:workflow',
		title: 'Automation',
		tagline:
			'Replace manual, repetitive work with systems that run themselves.',
		stack: ['Python', 'Node.js', 'n8n', 'REST/GraphQL APIs'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Python', 'Node.js', 'TypeScript'] },
			{
				group: 'FRAMEWORK',
				items: ['n8n', 'Temporal', 'BullMQ', 'Custom workers'],
			},
			{ group: 'STATE CONTROL', items: ['PostgreSQL', 'Redis', 'Queues'] },
			{
				group: 'BACKEND CORE',
				items: ['REST/GraphQL APIs', 'Webhooks', 'OAuth', 'Secrets vault'],
			},
			{
				group: 'EVENT RUNTIME',
				items: ['Schedulers', 'Cron', 'Event triggers'],
			},
		],
		scope: [
			{
				id: 'SYNC',
				title: 'Cross-tool workflows',
				categoryTag: '#SYNC',
				description:
					'Automate across the tools you already use, CRMs, spreadsheets, databases, and SaaS, replacing Monday-morning copy-paste marathons.',
				tags: ['Webhooks', 'API sync', 'Bidirectional sync', 'Idempotency'],
			},
			{
				id: 'TRIGGER',
				title: 'Event & schedule triggers',
				categoryTag: '#ON',
				description:
					'Runs on a schedule, on a webhook, or when something changes in your database, precisely when it should, every time.',
				tags: ['Cron', 'Webhooks', 'DB triggers', 'SLA enforcement'],
			},
			{
				id: 'CUSTOM',
				title: 'Beyond the off-the-shelf',
				categoryTag: '#CUSTOM',
				description:
					'When Zapier/n8n hit their ceiling, custom automation steps close the gap without switching platforms.',
				tags: ['Custom nodes', 'Retries', 'Rate limits', 'Scoped access'],
			},
			{
				id: 'OPS',
				title: 'Failure-proofing',
				categoryTag: '#ALERT',
				description:
					'Alerts on failure, not silent breakage, every run is observable, retryable, and auditable.',
				tags: ['Alerts', 'Dead-letter queues', 'Run logs', 'Audit trail'],
			},
			{
				id: 'DELIVER',
				title: 'Data movement & delivery',
				categoryTag: '#PIPES',
				description:
					'Move data between platforms and deliver outputs where your team actually looks, Slack, email, storage, or a dashboard.',
				tags: ['ETL', 'Exports', 'Notifications', 'Formats'],
			},
			{
				id: 'SCALE',
				title: 'High-volume throughput',
				categoryTag: '#THREADS',
				description:
					'Thousands of jobs, concurrent workers, rate-limit aware execution, automation that scales past a hobby script.',
				tags: ['Concurrency', 'Queues', 'Backpressure', 'Checkpoints'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail:
					"Map exactly what's done by hand, how often, and the risk of failure.",
				output: 'Manual-process map',
			},
			{
				step: 'ARCHITECT',
				detail: 'Triggers, steps, error handling, and notification paths.',
				output: 'Pipeline blueprint',
			},
			{
				step: 'EXECUTE',
				detail:
					'Automate incrementally, starting at the highest-friction step.',
				output: 'Running automations',
			},
			{
				step: 'HARDEN',
				detail: 'Alerts, retries, and audit logs so nothing breaks silently.',
				output: 'Operations playbook',
			},
		],
		telemetry: [
			{ key: 'orchestrator', value: 'n8n · temporal' },
			{ key: 'trigger_surface', value: 'cron · webhooks · db' },
			{ key: 'failure_policy', value: 'retry·alert·dead_letter' },
			{ key: 'throughput', value: 'thousands job/day' },
		],
		relatedWork: [
			{
				title: '20Lab',
				slug: '20lab',
				badge: 'E-commerce automation',
				description:
					'Automated product-sync pipelines that keep an e-commerce catalog fresh across channels without a single manual export.',
				metrics: [
					{ label: 'Workflows automated', value: '8+' },
					{ label: 'Sync reliability', value: '99.9%' },
					{ label: 'Manual hours saved', value: '12/wk' },
				],
				tech: ['Python', 'Playwright', 'n8n', 'Supabase'],
			},
		],
	},
	{
		slug: 'ai-integration',
		category: 'AI',
		categoryLabel: '#AIFX',
		icon: 'lucide:sparkles',
		title: 'AI Integration',
		tagline:
			'Ship AI features that actually fit your product, not a bolted-on chat widget.',
		stack: ['OpenAI', 'Claude', 'Pinecone', 'pgvector', 'Next.js', 'Python'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Next.js', 'Python', 'TypeScript'] },
			{ group: 'REASONING', items: ['OpenAI', 'Claude', 'Embeddings'] },
			{
				group: 'FRAMEWORK',
				items: ['RAG pipelines', 'Prompt templates', 'Streaming'],
			},
			{
				group: 'STATE CONTROL',
				items: ['Pinecone', 'pgvector', 'Redis cache'],
			},
			{ group: 'BACKEND CORE', items: ['tRPC', 'Auth', 'Rate limiting'] },
		],
		scope: [
			{
				id: 'RAG',
				title: 'Retrieval-augmented generation',
				categoryTag: '#RAG',
				description:
					'Answer over your own data with grounded sources, citations, and fresh content, no hallucinations on stale docs.',
				tags: ['Chunking', 'Semantic search', 'Hybrid rerank', 'Freshness'],
			},
			{
				id: 'EMBED',
				title: 'Vector search',
				categoryTag: '#VECTORS',
				description:
					'Semantic search across documents, products, or support tickets, set up, indexed, and tuned for relevance.',
				tags: ['Pinecone', 'pgvector', 'Re-ranking', 'Evaluation'],
			},
			{
				id: 'FLOW',
				title: 'Product workflow AI',
				categoryTag: '#PRODUCT',
				description:
					'Drop AI into existing user flows, summarization, classification, extraction, drafting, and delegating to a human at the right moments.',
				tags: ['Structured output', 'Guardrails', 'Human hand-off'],
			},
			{
				id: 'COST',
				title: 'Cost-aware architecture',
				categoryTag: '#BUDGET',
				description:
					"Caching, streaming, model routing, and rate limits tuned so the AI feature doesn't become a line-item surprise.",
				tags: ['Caching', 'Prompt optimization', 'Model routing'],
			},
			{
				id: 'OPS',
				title: 'LLM Ops',
				categoryTag: '#LLMOPS',
				description:
					'Version-controlled prompts, evals before deploys, and output-quality monitoring that keeps the feature honest.',
				tags: ['Prompt versioning', 'Evals', 'Guardrails', 'Dashboards'],
			},
			{
				id: 'DASH',
				title: 'Structured output handling',
				categoryTag: '#SCHEMA',
				description:
					'Typed, schema-validated model responses, not free-form JSON you have to trust, wired straight into your backend.',
				tags: ['Zod schemas', 'Tool calling', 'Retries', 'Validation'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail: 'What should the AI do, and where does it hand off to a human?',
				output: 'Use-case map',
			},
			{
				step: 'ARCHITECT',
				detail: 'Choose model, retrieval, caching, and streaming strategy.',
				output: 'AI architecture spec',
			},
			{
				step: 'EXECUTE',
				detail: 'Prototype fast, then productionize: errors, streams, costs.',
				output: 'Working AI feature',
			},
			{
				step: 'HARDEN',
				detail: 'Evals, monitoring, and output-quality tracking in production.',
				output: 'Quality dashboard',
			},
		],
		telemetry: [
			{ key: 'surface', value: 'product_embedded_flows' },
			{ key: 'retrieval_engine', value: 'hybrid_rag' },
			{ key: 'inference_gate', value: 'route·cache·stream' },
			{ key: 'validation', value: 'zod_structured_output' },
		],
		relatedWork: [
			{
				title: 'GPT Guider',
				slug: 'gpt-guider',
				badge: 'AI learning companion',
				description:
					'Every lesson plan and answer streams through a RAG pipeline over the curriculum, with layered prompts tuned per user level.',
				metrics: [
					{ label: 'Answer grounding', value: '100%' },
					{ label: 'Stream start', value: '<250ms' },
					{ label: 'Cost / session', value: '-62%' },
				],
				tech: ['RAG', 'OpenAI', 'Streaming', 'pgvector'],
			},
		],
	},
	{
		slug: 'mcp-server-development',
		category: 'AI',
		categoryLabel: '#MCP',
		icon: 'lucide:plug',
		title: 'MCP Server Development',
		tagline: 'Make your product usable from Claude, ChatGPT and Cursor.',
		stack: [
			'TypeScript',
			'MCP SDK',
			'OAuth 2.1',
			'Cloudflare Workers',
			'Python',
		],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['TypeScript', 'Python', 'Node.js'] },
			{
				group: 'REASONING',
				items: ['Claude', 'ChatGPT', 'Cursor clients'],
			},
			{ group: 'FRAMEWORK', items: ['MCP SDK', 'Tools', 'Resources'] },
			{
				group: 'STATE CONTROL',
				items: ['OAuth 2.1', 'Scopes', 'Sessions'],
			},
			{
				group: 'BACKEND CORE',
				items: ['Cloudflare Workers', 'Rate limiting', 'Auth'],
			},
		],
		scope: [
			{
				id: 'DESIGN',
				title: 'Tool and resource design',
				categoryTag: '#MCP',
				description:
					'Your SaaS or internal tool modeled as clean MCP tools and resources an agent can actually use.',
				tags: ['OpenAPI', 'Schema', 'Versioning'],
			},
			{
				id: 'REMOTE',
				title: 'Remote server, not localhost',
				categoryTag: '#SOCKET',
				description:
					'Deployed MCP server with streaming transport, reachable from any client.',
				tags: ['Deployment', 'CI/CD', 'Run logs'],
			},
			{
				id: 'AUTH',
				title: 'Auth that clients trust',
				categoryTag: '#SECURE',
				description:
					'OAuth 2.1 flows scoped per user, so agents act with least privilege.',
				tags: ['OAuth 2', 'Scoped access', 'Least privilege'],
			},
			{
				id: 'LIMITS',
				title: 'Rate limiting and quotas',
				categoryTag: '#BUDGET',
				description:
					'Per-key limits and usage metering so one agent cannot burn your budget.',
				tags: ['Rate limits', 'Metering', 'Cost caps'],
			},
			{
				id: 'TEST',
				title: 'Testing with real clients',
				categoryTag: '#TRACE',
				description:
					'Verified against Claude, ChatGPT and Cursor before docs ship.',
				tags: ['E2E', 'Contract tests', 'Evaluation'],
			},
			{
				id: 'DOCS',
				title: 'Docs and listing README',
				categoryTag: '#SPEC',
				description:
					'Integration guides and a listing-ready README that pass review.',
				tags: ['Documentation', 'Onboarding', 'Review-safe'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail: 'Which actions agents get, and which stay human-only.',
				output: 'Tool surface map',
			},
			{
				step: 'ARCHITECT',
				detail: 'Tool schemas, auth model and transport design.',
				output: 'MCP architecture spec',
			},
			{
				step: 'EXECUTE',
				detail: 'Build against real clients, not the inspector alone.',
				output: 'Working MCP server',
			},
			{
				step: 'HARDEN',
				detail: 'Quotas, logging and listing docs before launch.',
				output: 'Listing-ready release',
			},
		],
		telemetry: [
			{ key: 'transport', value: 'remote_sse_stream' },
			{ key: 'auth', value: 'oauth2_1_scoped' },
			{ key: 'api_surface', value: 'tools · resources' },
			{ key: 'validation', value: 'real_client_tested' },
		],
		relatedWork: [
			{
				title: 'GPT Guider',
				slug: 'gpt-guider',
				badge: 'Tool-using AI tutor',
				description:
					'A tutor that plans with tools and memory behind a cost-capped router, traced end to end.',
				metrics: [
					{ label: 'Task accuracy', value: '99.4%' },
					{ label: 'Response latency', value: '<450ms' },
					{ label: 'Uncaught loops', value: '0' },
				],
				tech: ['OpenAI', 'LangChain', 'Next.js', 'Streaming'],
			},
		],
	},
	{
		slug: 'mvp-sprint',
		category: 'Full-Stack',
		categoryLabel: '#MVP',
		icon: 'lucide:rocket',
		title: 'Fixed-Price MVP Sprint',
		tagline: 'Idea to launched product in 2 to 4 weeks.',
		stack: ['Next.js', 'Supabase', 'Stripe', 'Tailwind', 'Vercel'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Next.js', 'TypeScript', 'Tailwind'] },
			{ group: 'FRAMEWORK', items: ['Supabase', 'Auth', 'Stripe'] },
			{
				group: 'STATE CONTROL',
				items: ['PostgreSQL', 'Migrations', 'Backups'],
			},
			{ group: 'BACKEND CORE', items: ['tRPC', 'Edge functions', 'Webhooks'] },
			{ group: 'EVENT RUNTIME', items: ['Vercel', 'CI/CD', 'Previews'] },
		],
		scope: [
			{
				id: 'DISCOVER',
				title: 'Scoped discovery',
				categoryTag: '#MVP',
				description:
					'Two calls and one doc: users, core loop, and what is explicitly out of scope.',
				tags: ['Scope lock', 'Fixed price', 'Documentation'],
			},
			{
				id: 'DEMOS',
				title: 'Weekly demos',
				categoryTag: '#LIVE',
				description:
					'Working build in your hands every week, feedback folded in while it is cheap.',
				tags: ['Previews', 'CI/CD', 'Notifications'],
			},
			{
				id: 'CORE',
				title: 'Core loop first',
				categoryTag: '#FULL',
				description:
					'Auth, database and the one workflow that makes the product valuable.',
				tags: ['Auth', 'Database', 'Stripe'],
			},
			{
				id: 'DEPLOY',
				title: 'Production deploy',
				categoryTag: '#SHIP',
				description:
					'Live URL from week one, with analytics and error tracking wired in.',
				tags: ['Vercel', 'Deployment', 'Metrics'],
			},
			{
				id: 'ANALYTICS',
				title: 'Analytics',
				categoryTag: '#TRACE',
				description:
					'Know what users do from day one, not after the budget is gone.',
				tags: ['Dashboards', 'Metrics', 'Funnel tracking'],
			},
			{
				id: 'SUPPORT',
				title: '14 days post-launch support',
				categoryTag: '#CARE',
				description:
					'Fixes and small tweaks included after launch, then a clean handoff.',
				tags: ['Bug triage', 'Regression tests', 'Documentation'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail: 'Lock scope, timeline and fixed price in one doc.',
				output: 'Sprint contract',
			},
			{
				step: 'ARCHITECT',
				detail: 'Data model, auth and UI skeleton in days.',
				output: 'Clickable skeleton',
			},
			{
				step: 'EXECUTE',
				detail: 'Weekly demos, core loop first.',
				output: 'Live product URL',
			},
			{
				step: 'HARDEN',
				detail: 'Analytics, hardening and 14-day support.',
				output: 'Launch handoff',
			},
		],
		telemetry: [
			{ key: 'sprint_length', value: '2_to_4_weeks' },
			{ key: 'demos', value: 'weekly_working_build' },
			{ key: 'deploy', value: 'vercel_production' },
			{ key: 'support', value: '14_days_included' },
		],
		relatedWork: [
			{
				title: 'Pie Coin',
				slug: 'pie-coin',
				badge: 'Zero-to-shipped product',
				description:
					'From idea to a live engagement app with auth and real-time leaderboards, launched to production.',
				metrics: [
					{ label: 'Community members', value: '2K+' },
					{ label: 'Real-time events', value: 'Live' },
					{ label: 'Deployment', value: 'Production' },
				],
				tech: ['Next.js', 'Supabase', 'tRPC'],
			},
		],
	},
	{
		slug: 'full-stack-web-apps',
		category: 'Full-Stack',
		categoryLabel: '#SHIPS',
		icon: 'lucide:layers',
		title: 'Full-Stack Web Apps',
		tagline:
			'From database to deployed UI, a complete product, not just a frontend.',
		stack: ['Next.js', 'PostgreSQL', 'Supabase', 'Sanity', 'tRPC'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Next.js', 'TypeScript', 'React'] },
			{
				group: 'FRAMEWORK',
				items: ['TanStack Router', 'Tailwind CSS', 'tRPC'],
			},
			{
				group: 'STATE CONTROL',
				items: ['Zustand', 'TanStack Query', 'Server state'],
			},
			{
				group: 'BACKEND CORE',
				items: ['PostgreSQL', 'Supabase', 'Sanity CMS', 'Auth', 'Payments'],
			},
			{ group: 'EVENT RUNTIME', items: ['Edge functions', 'Webhooks', 'CRON'] },
		],
		scope: [
			{
				id: 'FULL',
				title: 'Complete architecture',
				categoryTag: '#FULL',
				description:
					'Frontend, backend, database, and auth designed as one system, not a frontend bolted onto a toy API.',
				tags: ['Next.js', 'Database', 'Auth', 'Deployment'],
			},
			{
				id: 'CMS',
				title: 'Headless CMS',
				categoryTag: '#CONTENT',
				description:
					'Sanity/Strapi wired in when content must stay editable by non-developers, live preview included.',
				tags: ['Sanity', 'Strapi', 'Live preview', 'Editor roles'],
			},
			{
				id: 'MOTION',
				title: 'Interaction & animation',
				categoryTag: '#MOTION',
				description:
					'Complex animation, scroll narrative, and interaction craft where the product is the experience.',
				tags: ['Motion', 'View transitions', 'Scroll-driven', 'Performance'],
			},
			{
				id: 'DEPLOY',
				title: 'Production deployment',
				categoryTag: '#LIVE',
				description:
					'Deployed on Vercel or your infra of choice with CI, env hygiene, previews, and rollbacks.',
				tags: ['Vercel', 'CI/CD', 'Previews', 'Env containers'],
			},
			{
				id: 'SPEED',
				title: 'Performance budget',
				categoryTag: '#FAST',
				description:
					'Core Web Vitals as a constraint, not an afterthought, hydration, bundling, and caching tuned for real users.',
				tags: ['LCP', 'Hydration', 'Code splitting', 'Edge caching'],
			},
			{
				id: 'SELL',
				title: 'Monetize it',
				categoryTag: '#REVENUE',
				description:
					'Stripe subscriptions, usage metering, and gate logic so the product can pay for itself from day one.',
				tags: ['Stripe', 'Subscriptions', 'Metering', 'Billing'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail:
					'Data model, user flows, and MVP boundaries locked before code.',
				output: 'Product map',
			},
			{
				step: 'ARCHITECT',
				detail:
					'Backend + frontend + data model built in parallel and integrated early.',
				output: 'System architecture',
			},
			{
				step: 'EXECUTE',
				detail:
					'Ship the core loop first, then layer CMS, motion, and commerce.',
				output: 'Working product',
			},
			{
				step: 'HARDEN',
				detail: 'Deploy, monitor, and hand over with docs and a runbook.',
				output: 'Live & monitored app',
			},
		],
		telemetry: [
			{ key: 'stack', value: 'nextjs · tRPC · postgres' },
			{ key: 'content', value: 'sanity_cms' },
			{ key: 'payments', value: 'stripe_subscriptions' },
			{ key: 'deploy', value: 'vercel · edge' },
		],
		relatedWork: [
			{
				title: 'Pie Coin',
				slug: 'pie-coin',
				badge: 'Full-stack web app',
				description:
					'A gamified engagement web app with auth, live leaderboards, and real-time updates on a battery-packed build.',
				metrics: [
					{ label: 'Community members', value: '2K+' },
					{ label: 'Real-time events', value: 'Live' },
					{ label: 'Deployment', value: 'Production' },
				],
				tech: ['Next.js', 'Supabase', 'tRPC'],
			},
			{
				title: '20Lab',
				slug: '20lab',
				badge: 'E-commerce platform',
				description:
					'Automated e-commerce infrastructure with product sync, live storefronts, and 24/7 operations.',
				metrics: [
					{ label: 'Storefronts synced', value: 'Live' },
					{ label: 'Uptime', value: '99.9%' },
					{ label: 'Pipeline', value: '24/7' },
				],
				tech: ['Next.js', 'Supabase', 'Python'],
			},
		],
	},
	{
		slug: 'mobile-app-development',
		category: 'Mobile',
		categoryLabel: '#MOBILE',
		icon: 'lucide:smartphone',
		title: 'Mobile App Development',
		tagline: 'Android, iOS, or one cross-platform codebase for both.',
		stack: ['React Native', 'Expo', 'Kotlin', 'Swift', 'Redux Toolkit'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['React Native', 'Expo', 'TypeScript'] },
			{
				group: 'FRAMEWORK',
				items: ['Tamagui', 'React Navigation', 'Expo Router'],
			},
			{
				group: 'STATE CONTROL',
				items: ['Zustand', 'Redux Toolkit', 'React Query', 'MMKV'],
			},
			{
				group: 'BACKEND CORE',
				items: ['Supabase', 'tRPC', 'Push notifications'],
			},
			{
				group: 'EVENT RUNTIME',
				items: ['App Store', 'Play Store', 'CodePush'],
			},
		],
		scope: [
			{
				id: 'CROSS',
				title: 'Cross-platform builds',
				categoryTag: '#BOTH',
				description:
					'One codebase that ships to both App Store and Play Store via React Native/Expo, faster to launch, cheaper to maintain.',
				tags: ['Expo', 'CodePush', 'Shared UI', 'JS core'],
			},
			{
				id: 'NATIVE',
				title: 'Native when it counts',
				categoryTag: '#METAL',
				description:
					'Kotlin/Swift modules exactly where platform performance or APIs demand them, camera, sensors, biometrics, bluetooth.',
				tags: ['Kotlin', 'Swift', 'Native modules', 'Device APIs'],
			},
			{
				id: 'OFFLINE',
				title: 'Offline-first architecture',
				categoryTag: '#OFFLINE',
				description:
					'Works on a train, in a tunnel, or on airplane mode, local persistence with graceful sync when connectivity returns.',
				tags: ['MMKV', 'Background sync', 'Conflict handling'],
			},
			{
				id: 'NOTIFY',
				title: 'Push & engagement',
				categoryTag: '#PUSH',
				description:
					'Notification pipelines, deep links, and in-app purchases wired end-to-end, not just registered tokens.',
				tags: ['APNs', 'FCM', 'Deep links', 'IAP'],
			},
			{
				id: 'SHIP',
				title: 'Store submission',
				categoryTag: '#STORE',
				description:
					'Full App Store / Play Store submission, review readiness, assets, metadata, and store compliance handled.',
				tags: ['Store assets', 'Review evasion', 'Compliance'],
			},
			{
				id: 'POLISH',
				title: 'Platform-grade polish',
				categoryTag: '#UX',
				description:
					"Haptics, transitions, and gestures tuned to each platform's conventions, nothing feels web-in-a-shell.",
				tags: ['Reanimated', 'Haptics', 'Platform UX', 'A11y'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail:
					'Platform decision, cross-platform vs native against your real requirements.',
				output: 'Platform strategy',
			},
			{
				step: 'ARCHITECT',
				detail:
					'Navigation model, state layer, offline sync, and API contract.',
				output: 'App architecture diagram',
			},
			{
				step: 'EXECUTE',
				detail:
					'Core features first, platform-specific polish second, real-device tested.',
				output: 'Tested app build',
			},
			{
				step: 'HARDEN',
				detail:
					'Store submission, review process, crash monitoring, launch support.',
				output: 'Live store listing',
			},
		],
		telemetry: [
			{ key: 'target_platforms', value: 'ios · android' },
			{ key: 'build_runtime', value: 'expo_sdk_52' },
			{ key: 'state_control', value: 'zustand · react_query' },
			{ key: 'release_channel', value: 'store · codepush' },
		],
	},
	{
		slug: 'ai-image-pod-automation',
		category: 'Automation',
		categoryLabel: '#POD',
		icon: 'lucide:image',
		title: 'AI Image and Print-on-Demand Automation',
		tagline:
			'Generate designs, mockups and listings, then publish to your store automatically.',
		stack: [
			'OpenAI Image',
			'Gemini Image',
			'Flux',
			'Printify',
			'Shopify',
			'Printful',
			'Etsy API',
			'Python',
		],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Python', 'Node.js', 'TypeScript'] },
			{
				group: 'REASONING',
				items: ['OpenAI Image', 'Gemini Image', 'Flux'],
			},
			{ group: 'FRAMEWORK', items: ['Printify', 'Printful', 'Shopify'] },
			{
				group: 'STATE CONTROL',
				items: ['Etsy API', 'Queues', 'Asset buckets'],
			},
			{
				group: 'BACKEND CORE',
				items: ['Webhooks', 'Schedulers', 'Admin dashboard'],
			},
		],
		scope: [
			{
				id: 'PIPELINE',
				title: 'Prompt-to-design pipeline',
				categoryTag: '#POD',
				description:
					'Niche prompts become finished designs in bulk, with style presets you control.',
				tags: ['Batch runs', 'Style presets', 'Versioning'],
			},
			{
				id: 'CLEANUP',
				title: 'Background removal and upscaling',
				categoryTag: '#SCHEMA',
				description:
					'Clean cutouts and print-resolution files, checked before they ship.',
				tags: ['Quality gates', 'Validation', 'Exports'],
			},
			{
				id: 'MOCKUPS',
				title: 'Realistic mockups',
				categoryTag: '#MOCK',
				description:
					'Designs on tees, mugs and posters with color variants, rendered automatically. Same pipeline also does product photos and ad creatives for brands.',
				tags: ['Color variants', 'Batch runs', 'Previews'],
			},
			{
				id: 'LISTINGS',
				title: 'Auto-written listings',
				categoryTag: '#CONTENT',
				description:
					'Titles, tags and descriptions written per platform, backed by niche and trend research.',
				tags: ['Keyword research', 'Structured output', 'Dedup'],
			},
			{
				id: 'PUBLISH',
				title: 'Bulk upload to stores',
				categoryTag: '#SYNC',
				description:
					'Push to Printify, Printful, Shopify and Etsy with one approval step.',
				tags: ['API sync', 'Cron', 'Run logs'],
			},
			{
				id: 'COMPLY',
				title: 'Screening and AI disclosure',
				categoryTag: '#COMPLY',
				description:
					'Trademark and copyright screening plus platform AI-disclosure rules handled.',
				tags: ['Source licensing', 'Compliance', 'Audit trail'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail:
					'Niches, products and stores, plus what good output looks like.',
				output: 'Niche brief',
			},
			{
				step: 'ARCHITECT',
				detail: 'Prompt system, QA gates and publish flow.',
				output: 'Pipeline blueprint',
			},
			{
				step: 'EXECUTE',
				detail: 'Generate, mock up and list in bulk.',
				output: 'Live listings',
			},
			{
				step: 'HARDEN',
				detail: 'Screening, disclosure and trend refresh loops.',
				output: 'Operations playbook',
			},
		],
		telemetry: [
			{ key: 'image_stack', value: 'openai · gemini · flux' },
			{ key: 'print_network', value: 'printify · printful' },
			{ key: 'storefronts', value: 'shopify · etsy' },
			{ key: 'qa_gate', value: 'screen·disclose·upscale' },
		],
		relatedWork: [
			{
				title: '20Lab',
				slug: '20lab',
				badge: 'Catalog-to-storefront pipeline',
				description:
					'Automated product pipelines that keep listings fresh across channels, synced live around the clock.',
				metrics: [
					{ label: 'Storefronts synced', value: 'Live' },
					{ label: 'Uptime', value: '99.9%' },
					{ label: 'Pipeline', value: '24/7' },
				],
				tech: ['Next.js', 'Supabase', 'Python'],
			},
		],
	},
	{
		slug: 'ecommerce-amazon-tools',
		category: 'Automation',
		categoryLabel: '#SELL',
		icon: 'lucide:shopping-cart',
		title: 'E-commerce and Amazon Seller Tooling',
		tagline: 'Listing, pricing and inventory automation for online sellers.',
		stack: [
			'Amazon SP-API',
			'Shopify API',
			'WooCommerce',
			'Python',
			'Node.js',
			'Playwright',
		],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Python', 'Node.js', 'TypeScript'] },
			{
				group: 'FRAMEWORK',
				items: ['Amazon SP-API', 'Shopify API', 'WooCommerce'],
			},
			{
				group: 'STATE CONTROL',
				items: ['PostgreSQL', 'Redis', 'Queues'],
			},
			{
				group: 'BACKEND CORE',
				items: ['REST/GraphQL APIs', 'Webhooks', 'OAuth'],
			},
			{
				group: 'EVENT RUNTIME',
				items: ['Schedulers', 'Cron', 'Event triggers'],
			},
		],
		scope: [
			{
				id: 'LISTINGS',
				title: 'Listing and keyword optimization',
				categoryTag: '#SELL',
				description:
					'Titles, bullets and backend terms tuned per marketplace, refreshed on schedule.',
				tags: ['Keyword research', 'Freshness', 'Dedup'],
			},
			{
				id: 'MONITOR',
				title: 'Price and review monitoring',
				categoryTag: '#ALERT',
				description:
					'Competitor prices and new reviews tracked, alerts before they cost you.',
				tags: ['Alerts', 'Cron', 'Notifications'],
			},
			{
				id: 'SYNC',
				title: 'Inventory sync',
				categoryTag: '#SYNC',
				description:
					'Stock levels synced across Amazon, Shopify and WooCommerce, oversells stopped.',
				tags: ['API sync', 'Idempotency', 'Conflict handling'],
			},
			{
				id: 'DASH',
				title: 'Reporting dashboards',
				categoryTag: '#PIPES',
				description: 'Sales, fees and ad spend in one view, refreshed daily.',
				tags: ['Dashboards', 'Metrics', 'Exports'],
			},
			{
				id: 'COPY',
				title: 'AI-generated listing copy',
				categoryTag: '#REVENUE',
				description:
					'Unique, policy-safe copy per SKU, no duplicate-content penalties.',
				tags: ['Structured output', 'Guardrails', 'Evaluation'],
			},
			{
				id: 'REPRICE',
				title: 'Guarded repricing',
				categoryTag: '#BUDGET',
				description:
					'Floor and ceiling guards with scheduled repricing, margin protected.',
				tags: ['Guardrails', 'Cost caps', 'Checkpoints'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail: 'Marketplaces, SKUs and the numbers that matter to you.',
				output: 'Seller map',
			},
			{
				step: 'ARCHITECT',
				detail: 'API access, sync rules and alert thresholds.',
				output: 'Tooling blueprint',
			},
			{
				step: 'EXECUTE',
				detail: 'Listings, sync and monitors live, one channel at a time.',
				output: 'Running tooling',
			},
			{
				step: 'HARDEN',
				detail: 'Guardrails, logs and daily reports before handoff.',
				output: 'Operations playbook',
			},
		],
		telemetry: [
			{ key: 'marketplaces', value: 'amazon · shopify · woo' },
			{ key: 'sync', value: 'hourly_stock_sync' },
			{ key: 'alerts', value: 'price · review_watch' },
			{ key: 'delivery', value: 'dashboards · csv' },
		],
		relatedWork: [
			{
				title: '20Lab',
				slug: '20lab',
				badge: 'E-commerce automation',
				description:
					'Automated product-sync pipelines that keep an e-commerce catalog fresh across channels without a single manual export.',
				metrics: [
					{ label: 'Workflows automated', value: '8+' },
					{ label: 'Sync reliability', value: '99.9%' },
					{ label: 'Manual hours saved', value: '12/wk' },
				],
				tech: ['Python', 'Playwright', 'n8n', 'Supabase'],
			},
		],
	},
	{
		slug: 'web-scraping',
		category: 'Data',
		categoryLabel: '#SCRAPE',
		icon: 'lucide:globe',
		title: 'Web Scraping & Data Extraction',
		tagline:
			"Structured data from any site, including ones that don't want to give it up easily.",
		stack: ['Python', 'Playwright', 'Scrapy', 'Proxy Infrastructure'],
		stackGroups: [
			{
				group: 'FOUNDATION',
				items: ['Python', 'Playwright', 'Scrapy', 'BeautifulSoup'],
			},
			{
				group: 'FRAMEWORK',
				items: ['Headless Chrome', 'Selenium', 'Async IO'],
			},
			{
				group: 'STATE CONTROL',
				items: ['Rotating proxies', 'Fingerprints', 'Sessions', 'Cache'],
			},
			{
				group: 'BACKEND CORE',
				items: ['PostgreSQL', 'Buckets', 'Streaming export', 'APIs'],
			},
			{
				group: 'EVENT RUNTIME',
				items: ['Schedulers', 'Retry policies', 'Queues'],
			},
		],
		scope: [
			{
				id: 'DYNAMIC',
				title: 'JavaScript-heavy sites',
				categoryTag: '#SPA',
				description:
					'Scrapers that survive infinite scroll, client-side rendering, and virtualized lists where plain HTTP requests come back empty.',
				tags: ['Headless browser', 'Infinite scroll', 'SPA rendering'],
			},
			{
				id: 'ANTIBOT',
				title: 'Anti-bot & CAPTCHA resistance',
				categoryTag: '#ARMOR',
				description:
					'Proxy rotation, fingerprint management, and CAPTCHA handling for sites engineered to resist scraping.',
				tags: [
					'Proxy rotation',
					'Fingerprints',
					'CAPTCHA flows',
					'Rate shaping',
				],
			},
			{
				id: 'SCHED',
				title: 'Scheduled pipelines',
				categoryTag: '#CADENCE',
				description:
					'Scrapes that run on a cadence and deliver fresh data on time, daily, weekly, or event-triggered.',
				tags: ['Cron', 'Incremental runs', 'Diffing', 'Delivery'],
			},
			{
				id: 'CLEAN',
				title: 'Clean, structured output',
				categoryTag: '#SCHEMA',
				description:
					'Lead, listing, and price data normalized into CSV, JSON, or your database, ready for tools, not a data dump.',
				tags: ['Normalization', 'Dedup', 'Schema', 'Validation'],
			},
			{
				id: 'ETHICS',
				title: 'Legal-safe methodology',
				categoryTag: '#COMPLY',
				description:
					"Robots.txt-aware, rate-limit-respecting, and account-safe extraction that won't put your operation in the gray zone.",
				tags: ['Robots.txt', 'ToS review', 'Rate limits', 'Source licensing'],
			},
			{
				id: 'SCALE',
				title: 'Distributed extraction',
				categoryTag: '#POOL',
				description:
					"Handles hundreds of sources and millions of pages with a proxy pool and workers that won't trip alarms.",
				tags: ['Proxy pool', 'Worker pools', 'Sharding', 'Bypass radios'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail:
					'Target analysis, structure, protections, rate limits, and consent.',
				output: 'Target feasibility report',
			},
			{
				step: 'ARCHITECT',
				detail: 'Parser + renderer strategy, proxy policy, and output schema.',
				output: 'Extraction design',
			},
			{
				step: 'EXECUTE',
				detail:
					'Build against pagination, dynamic content, and anti-bot measures.',
				output: 'Working extractor',
			},
			{
				step: 'HARDEN',
				detail:
					'Scheduled runs, retries, and drift detection on the target sites.',
				output: 'Watched pipeline',
			},
		],
		telemetry: [
			{ key: 'renderer', value: 'playwright_headless' },
			{ key: 'network_layer', value: 'rotating_proxy_pool' },
			{ key: 'captcha_policy', value: 'residential_bypass' },
			{ key: 'delivery', value: 'csv·json·db_push' },
		],
	},
	{
		slug: 'browser-extensions',
		category: 'Extensions',
		categoryLabel: '#EXT',
		icon: 'lucide:puzzle',
		title: 'Browser Extensions',
		tagline: 'Custom Chrome, Firefox, and Edge extensions, built to spec.',
		stack: ['TypeScript', 'Manifest V3', 'React', 'Vite'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['TypeScript', 'JavaScript', 'React'] },
			{
				group: 'FRAMEWORK',
				items: ['Manifest V3', 'Vite', 'Plasmo', 'CRX packaging'],
			},
			{
				group: 'STATE CONTROL',
				items: ['chrome.storage', 'IndexedDB', 'Sync'],
			},
			{
				group: 'BACKEND CORE',
				items: ['Content scripts', 'Background workers', 'Message passing'],
			},
			{
				group: 'EVENT RUNTIME',
				items: ['Web Store', 'Add-ons sites', 'Versioning'],
			},
		],
		scope: [
			{
				id: 'V3',
				title: 'Manifest V3 builds',
				categoryTag: '#MV3',
				description:
					'Modern manifest V3 architecture with service workers, no legacy patterns that get rejected at review.',
				tags: ['MV3', 'Service workers', 'Permissions', 'Review-safe'],
			},
			{
				id: 'CROSS',
				title: 'Cross-browser coverage',
				categoryTag: '#POLY',
				description:
					'One build that behaves consistently across Chrome, Firefox, and Edge, with browser-specific quirks handled.',
				tags: ['Polyfills', 'Quirk handling', 'Feature flags'],
			},
			{
				id: 'UX',
				title: 'Popup & settings UI',
				categoryTag: '#UI',
				description:
					'A real UI for your extension, popup, options, and onboarding in React, not bare HTML.',
				tags: ['React', 'Options page', 'Onboarding', 'i18n'],
			},
			{
				id: 'DATA',
				title: 'Content & background work',
				categoryTag: '#WORKER',
				description:
					"Content scripts that read pages, background workers that stay alive, and message passing that doesn't leak.",
				tags: ['Content scripts', 'Workers', 'Message passing', 'Storage'],
			},
			{
				id: 'SUBMIT',
				title: 'Store submission',
				categoryTag: '#STORE',
				description:
					'Packaging, review-readiness, store assets, and privacy disclosures handled end-to-end.',
				tags: ['Store assets', 'Privacy policy', 'Review flow'],
			},
			{
				id: 'SECURE',
				title: 'Security by default',
				categoryTag: '#HARDEN',
				description:
					'Least-privilege permissions, no remote code, and input sanitization, extensions that pass audit clean.',
				tags: ['Least privilege', 'No remote code', 'Sanitization'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail:
					'Define what it does and exactly where it injects or interacts.',
				output: 'Extension spec',
			},
			{
				step: 'ARCHITECT',
				detail:
					'Manifest, service worker, storage, and message-passing design.',
				output: 'Extension blueprint',
			},
			{
				step: 'EXECUTE',
				detail:
					'Core functionality first, popup/UI polish second, cross-browser tested.',
				output: 'Tested build',
			},
			{
				step: 'HARDEN',
				detail: 'Package, privacy review, and store submission support.',
				output: 'Live store listing',
			},
		],
		telemetry: [
			{ key: 'manifest', value: 'v3' },
			{ key: 'browsers', value: 'chrome · firefox · edge' },
			{ key: 'architecture', value: 'worker + content' },
			{ key: 'distribution', value: 'stores (3)' },
		],
	},
	{
		slug: 'programmatic-seo',
		category: 'Data',
		categoryLabel: '#SEO',
		icon: 'lucide:trending-up',
		title: 'Programmatic SEO and AI Content Systems',
		tagline:
			'Thousands of search-optimized pages, generated and kept fresh automatically.',
		stack: ['Next.js', 'Astro', 'Python', 'OpenAI', 'Claude', 'Supabase'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Next.js', 'Astro', 'TypeScript'] },
			{
				group: 'REASONING',
				items: ['OpenAI', 'Claude', 'Structured output'],
			},
			{
				group: 'FRAMEWORK',
				items: ['Page templates', 'Sitemaps', 'Schema.org'],
			},
			{
				group: 'STATE CONTROL',
				items: ['Supabase', 'PostgreSQL', 'Queues'],
			},
			{
				group: 'BACKEND CORE',
				items: ['Edge caching', 'Schedulers', 'Webhooks'],
			},
		],
		scope: [
			{
				id: 'DATA',
				title: 'Data sourcing',
				categoryTag: '#POOL',
				description:
					'Inventory, catalogs or public datasets cleaned into page-ready records.',
				tags: ['ETL', 'Dedup', 'Normalization'],
			},
			{
				id: 'PAGES',
				title: 'Templated page generation',
				categoryTag: '#SEO',
				description:
					'Thousands of fast pages from one template, each with unique URLs and internal links.',
				tags: ['Versioning', 'Pagination', 'Previews'],
			},
			{
				id: 'CONTENT',
				title: 'AI-written unique content',
				categoryTag: '#CONTENT',
				description:
					'Every page gets distinct copy with quality checks, no thin duplicates.',
				tags: ['Structured output', 'Evaluation', 'Freshness'],
			},
			{
				id: 'SCHEMA',
				title: 'Schema markup',
				categoryTag: '#SCHEMA',
				description:
					'Valid structured data per page type, tested before deploy.',
				tags: ['Schema', 'Validation', 'Contract tests'],
			},
			{
				id: 'INDEX',
				title: 'Sitemap and indexing automation',
				categoryTag: '#CADENCE',
				description:
					'Sitemaps split and submitted, internal links keep crawlers moving.',
				tags: ['Cron', 'Delivery', 'Run logs'],
			},
			{
				id: 'REFRESH',
				title: 'Freshness loops',
				categoryTag: '#SYNC',
				description:
					'Prices, availability and stats refresh on schedule so pages stay accurate.',
				tags: ['Incremental runs', 'Diffing', 'Checkpoints'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail: 'Data sources, page types and ranking targets.',
				output: 'SEO blueprint',
			},
			{
				step: 'ARCHITECT',
				detail: 'Templates, content rules and crawl budget plan.',
				output: 'Page architecture',
			},
			{
				step: 'EXECUTE',
				detail: 'Generate, validate and publish in batches.',
				output: 'Indexed pages',
			},
			{
				step: 'HARDEN',
				detail: 'Monitoring, refresh loops and quality gates.',
				output: 'Growth dashboard',
			},
		],
		telemetry: [
			{ key: 'page_engine', value: 'nextjs · astro' },
			{ key: 'content', value: 'ai_unique_checked' },
			{ key: 'structured_data', value: 'schema_validated' },
			{ key: 'indexing', value: 'sitemap_automated' },
		],
		relatedWork: [
			{
				title: '20Lab',
				slug: '20lab',
				badge: 'Fresh catalog content',
				description:
					'Catalog data kept fresh and structured across storefronts, ready for search and sync.',
				metrics: [
					{ label: 'Storefronts synced', value: 'Live' },
					{ label: 'Uptime', value: '99.9%' },
					{ label: 'Pipeline', value: '24/7' },
				],
				tech: ['Next.js', 'Supabase', 'Python'],
			},
		],
	},
	{
		slug: 'backend-development',
		category: 'Backend',
		categoryLabel: '#APIS',
		icon: 'lucide:server',
		title: 'Backend Development',
		tagline: 'APIs and services built for scale, not just for demos.',
		stack: [
			'Node.js',
			'NestJS',
			'Express.js',
			'Python',
			'FastAPI',
			'PostgreSQL',
		],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Node.js', 'TypeScript', 'Python'] },
			{
				group: 'FRAMEWORK',
				items: ['NestJS', 'Express.js', 'FastAPI', 'tRPC', 'GraphQL'],
			},
			{
				group: 'STATE CONTROL',
				items: ['PostgreSQL', 'Redis', 'Prisma', 'Drizzle'],
			},
			{
				group: 'BACKEND CORE',
				items: ['Auth (JWT/OAuth)', 'Rate limiting', 'WebSockets', 'Payments'],
			},
			{
				group: 'EVENT RUNTIME',
				items: ['BullMQ', 'Message queues', 'Webhooks'],
			},
		],
		scope: [
			{
				id: 'API',
				title: 'REST & GraphQL design',
				categoryTag: '#CONTRACT',
				description:
					'Endpoints, schemas, and contracts designed before implementation, APIs that frontend teams actually like consuming.',
				tags: ['OpenAPI', 'Versioning', 'Pagination', 'Error mapping'],
			},
			{
				id: 'AUTH',
				title: 'Auth & authorization',
				categoryTag: '#SECURE',
				description:
					"JWT, OAuth, session-based, and role-scoped access, plus refresh flows that don't get hacked on the frontend.",
				tags: ['JWT', 'OAuth 2', 'RBAC', 'Refresh tokens'],
			},
			{
				id: 'DB',
				title: 'Schema & query optimization',
				categoryTag: '#INDEX',
				description:
					'PostgreSQL schema design, indexes, and query planning so endpoints stay fast as data grows, not just at 10 rows.',
				tags: ['Migrations', 'Indexes', 'Query plans', 'Caching'],
			},
			{
				id: 'THIRD',
				title: 'Third-party integrations',
				categoryTag: '#SOCKET',
				description:
					'Payments, AI APIs, and external services wired with sane timeouts, retries, and failure isolation.',
				tags: ['Payments', 'AI providers', 'Retries', 'Circuit breakers'],
			},
			{
				id: 'OPS',
				title: 'Observability from day one',
				categoryTag: '#TRACE',
				description:
					'Structured logs, metrics, and tracing wired in from the first endpoint, not bolted on after an outage.',
				tags: ['Logging', 'Metrics', 'Tracing', 'Dashboards'],
			},
			{
				id: 'DOC',
				title: 'Docs & testing',
				categoryTag: '#SPEC',
				description:
					'Auto-generated docs and contract tests so consumers never guess, and breaking changes get caught in CI.',
				tags: ['Swagger', 'Contract tests', 'Unit tests', 'E2E'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail: 'Map endpoints, data flow, and integration points.',
				output: 'API contract',
			},
			{
				step: 'ARCHITECT',
				detail:
					'Right framework for the job, structured, lightweight, or AI-heavy.',
				output: 'Service architecture',
			},
			{
				step: 'EXECUTE',
				detail:
					'Tests, error handling, and logging from the very first endpoint.',
				output: 'Reviewed service',
			},
			{
				step: 'HARDEN',
				detail: 'CI-ready deploy, documentation, and production hardening.',
				output: 'Production deploy',
			},
		],
		telemetry: [
			{ key: 'runtime', value: 'node_20 · python_3.12' },
			{ key: 'api_surface', value: 'rest · graphql · tRPC' },
			{ key: 'data_layer', value: 'postgres · redis' },
			{ key: 'auth', value: 'jwt · oauth2 · rbac' },
		],
	},
	{
		slug: 'retainer',
		category: 'Backend',
		categoryLabel: '#CARE',
		icon: 'lucide:shield-check',
		title: 'Maintenance and Automation Retainer',
		tagline: 'A monthly engineer on call for your product and workflows.',
		stack: ['Node.js', 'Python', 'n8n', 'GitHub Actions', 'Sentry'],
		stackGroups: [
			{ group: 'FOUNDATION', items: ['Node.js', 'Python', 'TypeScript'] },
			{ group: 'FRAMEWORK', items: ['n8n', 'BullMQ', 'Custom workers'] },
			{
				group: 'STATE CONTROL',
				items: ['PostgreSQL', 'Redis', 'Queues'],
			},
			{
				group: 'BACKEND CORE',
				items: ['GitHub Actions', 'Sentry', 'Secrets vault'],
			},
			{ group: 'EVENT RUNTIME', items: ['Schedulers', 'Cron', 'Alerts'] },
		],
		scope: [
			{
				id: 'BUGS',
				title: 'Bug fixes',
				categoryTag: '#ALERT',
				description:
					'Production bugs triaged and fixed first, with regression tests attached.',
				tags: ['Bug triage', 'Regression tests', 'E2E'],
			},
			{
				id: 'FEATURES',
				title: 'Small features',
				categoryTag: '#FULL',
				description:
					'Scoped monthly capacity for the tweaks that never make a sprint.',
				tags: ['Versioning', 'Previews', 'Documentation'],
			},
			{
				id: 'MONITOR',
				title: 'Monitoring',
				categoryTag: '#TRACE',
				description:
					'Errors and downtime caught by Sentry and uptime checks, not by your users.',
				tags: ['Alerts', 'Run logs', 'Metrics'],
			},
			{
				id: 'UPDATES',
				title: 'Dependency and security updates',
				categoryTag: '#HARDEN',
				description:
					'Patches applied and verified monthly, breaking changes flagged early.',
				tags: ['CI/CD', 'Contract tests', 'Audit trail'],
			},
			{
				id: 'UPKEEP',
				title: 'Automation upkeep',
				categoryTag: '#CUSTOM',
				description:
					'Your n8n flows and scripts kept running as APIs change under them.',
				tags: ['Custom nodes', 'Retries', 'Checkpoints'],
			},
			{
				id: 'REPORT',
				title: 'Monthly report',
				categoryTag: '#SPEC',
				description:
					'What shipped, what broke, what is next, in one readable page.',
				tags: ['Dashboards', 'Delivery', 'Documentation'],
			},
		],
		process: [
			{
				step: 'SCOPE',
				detail: 'Systems covered, response times and monthly capacity.',
				output: 'Retainer agreement',
			},
			{
				step: 'ARCHITECT',
				detail: 'Access, monitoring and deploy paths set up once.',
				output: 'Ops baseline',
			},
			{
				step: 'EXECUTE',
				detail: 'Fixes and features shipped against a shared board.',
				output: 'Monthly changelog',
			},
			{
				step: 'HARDEN',
				detail: 'Updates, audits and a report at the end of each month.',
				output: 'Monthly report',
			},
		],
		telemetry: [
			{ key: 'coverage', value: 'product · workflows' },
			{ key: 'response', value: 'same_day_triage' },
			{ key: 'qa_gate', value: 'tests · ci_checks' },
			{ key: 'reporting', value: 'monthly_changelog' },
		],
		relatedWork: [
			{
				title: 'Pie Coin',
				slug: 'pie-coin',
				badge: 'Live product, kept live',
				description:
					'A production community app with real-time updates, monitored and maintained in production.',
				metrics: [
					{ label: 'Community members', value: '2K+' },
					{ label: 'Real-time events', value: 'Live' },
					{ label: 'Deployment', value: 'Production' },
				],
				tech: ['Next.js', 'Supabase', 'tRPC'],
			},
		],
	},
];

export function getService(slug: string) {
	return services.find((service) => service.slug === slug);
}
